// Table limits in the currency an operator types them in (#519).
//
// A table config stores every limit and chip as an integer count of its
// currency's MINOR unit (USD in cents, every other currency in whole units).
// The editor shows and accepts them in the currency itself — "1.50" for a
// dollar fifty — and these helpers convert between the two and state the stake
// grids in the currency, so no operator ever reads a bare count of cents.

import { formatMoney, parseMoney, toDecimalString } from '@apx/money';
import { STAKE_GRANULARITY_BY_GAME } from '@apx/integrator-config/schemas';

/** One currency's limits as the table config stores them: minor units. */
export interface TableAmounts {
  minBet: number;
  maxBet: number;
  chips: number[];
  sideBetMaxBet?: number;
}

/** The text a limit input shows for a stored amount: "1.50" in USD, "1000" in IDR. */
export function limitInputValue(minor: number, currency: string): string {
  return toDecimalString(minor, currency);
}

/**
 * Reads a limit input into minor units, as the table config stores them (a JS
 * number), or null when the text is not an amount in `currency`: not a number,
 * negative, or with more decimals than the currency has.
 */
export function readLimitInput(text: string, currency: string): number | null {
  const minor = parseMoney(text, currency);
  if (minor === null || minor > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return Number(minor);
}

/** "1.00 USD", "100 IDR": an amount with its currency, exact and never compact. */
export function amountWithCode(minor: number, currency: string): string {
  return `${formatMoney(minor, currency, { compact: false })} ${currency}`;
}

/**
 * Smallest stake on which a Dice Rush payout is exact, in minor units. Mirrors
 * the config schema's own grid and each Dice Rush game's `STAKE_GRID_CREDITS`;
 * `table-limits-money.test.ts` pins it to both game packages.
 */
export const DICERUSH_STAKE_GRID_MINOR = 25;

/**
 * Stake grids the table schemas enforce, in MINOR units: a stake off the grid
 * would have every win floored down. Crash checks both limits and every chip;
 * Dice Rush checks the base bet and every chip. The rule stays in minor units
 * — only the message states the amount in the currency.
 */
export const STAKE_GRIDS: Readonly<Record<string, { grid: number; checksMaxBet: boolean }>> = {
  crash: { grid: STAKE_GRANULARITY_BY_GAME.crash!, checksMaxBet: true },
  shortdicerush: { grid: DICERUSH_STAKE_GRID_MINOR, checksMaxBet: false },
  longdicerush: { grid: DICERUSH_STAKE_GRID_MINOR, checksMaxBet: false },
};

/**
 * Off-grid stakes for `gameId`, stated in the currency: a USD crash ladder must
 * be "a multiple of 1.00 USD", a USD Dice Rush ladder "a multiple of 0.25 USD".
 * Empty when the game has no grid or every stake is on it.
 */
export function stakeGridIssues(gameId: string, v: TableAmounts, currency: string): string[] {
  const rule = STAKE_GRIDS[gameId];
  if (!rule) return [];
  const { grid } = rule;
  const step = amountWithCode(grid, currency);
  const fields: Array<{ label: string; value: number }> = [
    { label: 'Base bet', value: v.minBet },
    ...(rule.checksMaxBet ? [{ label: 'Max bet', value: v.maxBet }] : []),
    ...v.chips.map((c, i) => ({ label: `Chip ${i + 1}`, value: c })),
  ];
  const off = fields.filter((f) => f.value % grid !== 0);
  if (off.length === 0) return [];
  if (gameId === 'crash') {
    return off.map(
      (f) =>
        `${f.label}: crash stakes must be a multiple of ${step} — ${amountWithCode(f.value, currency)} would underpay every win placed on it`,
    );
  }
  return [
    `Dice Rush stakes must be a multiple of ${step} or every win is silently rounded down: ` +
      off.map((f) => `${f.label} ${amountWithCode(f.value, currency)}`).join(', '),
  ];
}

/** Chips outside [base bet, max bet], stated in the currency. Every editable
 *  chip must be placeable; a slot outside the window is a dead chip. */
export function chipWindowIssues(v: TableAmounts, currency: string): string[] {
  // An inverted window is its own error, reported by the schema; listing every
  // chip as outside it would bury that.
  if (v.minBet > v.maxBet) return [];
  const outside = v.chips
    .map((c, i) => ({ label: `Chip ${i + 1}`, value: c }))
    .filter((c) => c.value < v.minBet || c.value > v.maxBet);
  if (outside.length === 0) return [];
  return [
    `every chip must sit between the base bet (${amountWithCode(v.minBet, currency)}) and the max bet ` +
      `(${amountWithCode(v.maxBet, currency)}) — outside: ` +
      outside.map((c) => `${c.label} ${amountWithCode(c.value, currency)}`).join(', '),
  ];
}
